import { useTranslation } from 'react-i18next';

import { ReactComponent as RightArrowIcon } from '../../../../../../assets/icons/arrow-right.svg';
import { ReactComponent as SuccessFill } from '../../../../../../assets/icons/success_fill.svg';
import CustomButton from '../../../../../../shared/components/Buttons/CustomButton/CustomButton';
import DataChecklist from '../../../../../../shared/components/Globals/DataChecklist/DataChecklist';
import ProfileCard from '../../../../../../shared/components/ProfileCards/ProfileCard/ProfileCard';
import { ACTIVATION_CHECKLIST_ITEM, CLICK_ACTION, SCOPE_PROFILE } from '../../../../../../shared/constants/tagManager';
import { useBroker } from '../../../../../../shared/hooks/useBroker/useBroker';
import type { IDataCheck } from '../../../../../../shared/interfaces/checks';
import type { IProspect } from '../../../../../../shared/interfaces/prospects';
import type { IQuote } from '../../../../../../shared/interfaces/quotes';
import TagManagerService from '../../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_PROSPECTS } from '../../../../../../shared/translations/prospects';
import { ProspectStatus } from '../../../../../../shared/types/prospects';
import {
  areAllChecksDone,
  buildActivationCheckGroups,
  isReadyToActivate
} from '../../../../../../shared/utils/checks/activationChecks';

import './ActivationReadinessCard.scss';

interface IActivationReadinessCardProps {
  prospect: IProspect;
  quotes?: IQuote[];
  loading?: boolean;
  onActivate?: (id: number) => void;
  onCheckClick?: (check: IDataCheck) => void;
}

const ActivationReadinessCard = ({
  prospect,
  quotes = [],
  loading = false,
  onActivate,
  onCheckClick
}: IActivationReadinessCardProps) => {
  const { t } = useTranslation();
  const { addonTypeById } = useBroker();

  const isActivating = prospect.status === ProspectStatus.PendingActivation;
  const activationQuotes = isActivating ? quotes : [];
  const groups = buildActivationCheckGroups(prospect, activationQuotes, addonTypeById);
  const allDone = areAllChecksDone(groups);
  const showSkeleton = loading && isActivating;
  // Readiness also demands an accepted, active quote: the client checks alone never activate a prospect.
  const showReady = !showSkeleton && isReadyToActivate(prospect, activationQuotes, addonTypeById);

  const handleCheckClick = (check: IDataCheck) => {
    if (check.analyticsTarget) {
      TagManagerService.pushEvent(CLICK_ACTION, ACTIVATION_CHECKLIST_ITEM, {
        ...SCOPE_PROFILE,
        target: check.analyticsTarget
      });
    }
    onCheckClick?.(check);
  };

  const renderChecklistFooter = () => {
    if (showSkeleton) {
      return (
        <div className="activation-readiness__skeleton" aria-hidden="true">
          <span className="activation-readiness__skeleton-title" />
          <span className="activation-readiness__skeleton-row" />
        </div>
      );
    }
    if (showReady) {
      return (
        <div className="activation-readiness__ready">
          <div className="activation-readiness__ready-title">
            <div className="activation-readiness__ready-icon">
              <SuccessFill />
            </div>
            {t(TRANSLATIONS_PROSPECTS.detail.pendingActivation.readyTitle)}
          </div>
          <div className="activation-readiness__ready-body">
            {t(TRANSLATIONS_PROSPECTS.detail.pendingActivation.readyBody)}
          </div>
          <CustomButton
            variant="primary"
            className="activation-readiness__ready-cta"
            onClick={() => onActivate?.(prospect.id)}
          >
            {t(TRANSLATIONS_PROSPECTS.detail.pendingActivation.activate)}
            <RightArrowIcon className="activation-readiness__ready-arrow" />
          </CustomButton>
        </div>
      );
    }
    if (isActivating) {
      return (
        <p className="activation-readiness__warn">
          <strong>{t(TRANSLATIONS_PROSPECTS.profile.activation.warningTitle)}</strong>{' '}
          {t(TRANSLATIONS_PROSPECTS.profile.activation.warningBody)}
        </p>
      );
    }
    if (allDone) return null;
    return <p className="activation-readiness__hint">{t(TRANSLATIONS_PROSPECTS.profile.activation.hint)}</p>;
  };

  return (
    <ProfileCard title={t(TRANSLATIONS_PROSPECTS.profile.activation.title)} className="activation-readiness">
      <DataChecklist groups={groups} onCheckClick={handleCheckClick} />
      {renderChecklistFooter()}
    </ProfileCard>
  );
};

export default ActivationReadinessCard;
